/**
 * A fork interrupted by signup, and where to resume it.
 *
 * "Make it mine" on a plan page needs an account. Rather than lose the click,
 * the page records what was being forked, sends the visitor to sign up, and
 * `useUserStore` — the first thing to know the account exists — sends them
 * back with `?fork=1` so the page reopens the fork modal instead of making
 * them find the button again.
 *
 * Two kinds of page fork: the public page (`/plans/p/<slug>`) and the share
 * page (`/shared/<token>`), which is the link the app hands out. An intent
 * carries exactly one of the two identifiers. Nothing here reaches analytics;
 * the token in particular is a capability and stays in localStorage only.
 */

export const FORK_INTENT_KEY = 'vizday_fork_intent';
const FORK_INTENT_TTL_MS = 3600000;

export interface ForkIntent {
  slug?: string;
  token?: string;
  ts: number;
}

export function storeForkIntent(planSlug: string) {
  localStorage.setItem(FORK_INTENT_KEY, JSON.stringify({ slug: planSlug, ts: Date.now() }));
}

/** The share-page variant: the token is the plan's identity there. */
export function storeSharedForkIntent(shareToken: string) {
  localStorage.setItem(FORK_INTENT_KEY, JSON.stringify({ token: shareToken, ts: Date.now() }));
}

export function getForkIntent(): ForkIntent | null {
  try {
    const raw = localStorage.getItem(FORK_INTENT_KEY);
    if (!raw) return null;
    const intent = JSON.parse(raw);
    // Expire after 1 hour
    if (Date.now() - intent.ts > FORK_INTENT_TTL_MS) {
      localStorage.removeItem(FORK_INTENT_KEY);
      return null;
    }
    return intent;
  } catch {
    localStorage.removeItem(FORK_INTENT_KEY);
    return null;
  }
}

export function clearForkIntent() {
  localStorage.removeItem(FORK_INTENT_KEY);
}

/**
 * Where a fresh session should land to finish the fork, or null when there is
 * nothing to finish. Pure, so the decision is testable apart from the store's
 * fetch flow. Accepts `unknown` because the input is parsed localStorage:
 * anything malformed, stale, or carrying neither identifier resolves to null
 * rather than to a path built from garbage.
 */
export function forkIntentResumePath(intent: unknown, now: number = Date.now()): string | null {
  if (!intent || typeof intent !== 'object') return null;
  const { slug, token, ts } = intent as Record<string, unknown>;
  // Same boundary as getForkIntent: an intent exactly one hour old still counts.
  if (typeof ts !== 'number' || !Number.isFinite(ts) || now - ts > FORK_INTENT_TTL_MS) return null;
  if (typeof token === 'string' && token) return `/shared/${encodeURIComponent(token)}?fork=1`;
  if (typeof slug === 'string' && slug) return `/plans/p/${encodeURIComponent(slug)}?fork=1`;
  return null;
}
